import React, { useEffect, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CircleIcon from '@mui/icons-material/Circle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PhaseDef } from './types';

interface Props {
  phases: PhaseDef[];
  currentPhase: string;
  content?: Record<string, React.ReactNode>;
  subtext?: Record<string, React.ReactNode>;
  // Any change to this key snaps the open section back to the current phase.
  syncKey?: string | number;
  // A phase whose status icon should render as a warning.
  warningPhase?: string;
}

const ICON_SIZE = 26;
const COLUMN_WIDTH = 32;
const LINE_COLOR = 'grey.300';

const PatientPhaseAccordion: React.FC<Props> = ({ phases, currentPhase, content, subtext, syncKey, warningPhase }) => {
  const currentIndex = phases.findIndex(p => p.key === currentPhase);
  const [openIndex, setOpenIndex] = useState<number>(currentIndex);

  useEffect(() => {
    setOpenIndex(currentIndex);
  }, [currentIndex, syncKey]);

  const renderIcon = (idx: number) => {
    const phase = phases[idx];
    if (warningPhase === phase.key) {
      return (
        <Box
          sx={{
            width: ICON_SIZE, height: ICON_SIZE, borderRadius: '50%',
            bgcolor: '#fff4e5',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: ICON_SIZE - 8, color: '#ed6c02' }} />
        </Box>
      );
    }
    if (idx < currentIndex) return <CheckCircleIcon sx={{ color: phase.color, fontSize: ICON_SIZE }} />;
    if (idx === currentIndex) return <CircleIcon sx={{ color: phase.color, fontSize: ICON_SIZE }} />;
    return <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: ICON_SIZE }} />;
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: '#fff',
        p: 3,
      }}
    >
      {phases.map((phase, idx) => {
        const isLast = idx === phases.length - 1;
        const isCurrent = idx === currentIndex;
        const isOpen = openIndex === idx;
        const isInteractive = phase.interactive !== false;

        return (
          <Box
            key={phase.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: `${COLUMN_WIDTH}px 1fr`,
              alignItems: 'stretch',
            }}
          >
            {/* Icon column: icon on top, line filler below */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ lineHeight: 0 }}>{renderIcon(idx)}</Box>
              {!isLast && <Box sx={{ flex: 1, width: '2px', bgcolor: LINE_COLOR, minHeight: 8 }} />}
            </Box>

            {/* Right column: header (clickable) + collapsible content */}
            <Box sx={{ pb: isLast ? 0 : 3 }}>
              <Box
                onClick={isInteractive ? () => setOpenIndex(prev => (prev === idx ? -1 : idx)) : undefined}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  columnGap: 0.5,
                  alignItems: 'center',
                  cursor: isInteractive ? 'pointer' : 'default',
                  borderRadius: 1,
                  px: 1,
                  ml: -1,
                  py: 0.25,
                  transition: 'background-color 0.15s ease',
                  ...(isInteractive && { '&:hover': { bgcolor: 'action.hover' } }),
                }}
              >
                {isInteractive ? (
                  <ExpandMoreIcon
                    sx={{
                      color: 'text.secondary',
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                ) : (
                  <ExpandMoreIcon sx={{ visibility: 'hidden' }} />
                )}
                <Typography
                  variant="subtitle1"
                  fontWeight={isCurrent ? 700 : 600}
                  sx={{ color: 'text.primary', lineHeight: 1.3, fontSize: '1.15rem' }}
                >
                  {phase.label}
                </Typography>
                {subtext?.[phase.key] && !isOpen && (
                  <>
                    <Box />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {subtext[phase.key]}
                    </Typography>
                  </>
                )}
              </Box>

              <Collapse in={isOpen && isInteractive} timeout="auto" unmountOnExit>
                <Box sx={{ pl: '28px', py: 1 }}>
                  {content?.[phase.key] ?? (
                    <Typography variant="body2" color="text.secondary">
                      {phase.label} content goes here.
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default PatientPhaseAccordion;
